import { v4 as uuidv4 } from 'uuid';
import { BOMPageData, ProductConfig, WhereUsedItem } from '@cpak/interfaces';
import {
  AccountModel,
  CountryConfigData,
  PlantConfigData,
  ProductPageData,
  PrototypePageData,
  QuoteConfigData,
  QuotePageData,
  QuoteProductModel,
  SistersConfigData,
  StartResponsePayload,
} from '@cpak/adp-cpak-bff-service-models';
import { ExternalIdModel } from '@cpak/adp-cpak-accounts-service-models';
import {
  AssetModel,
  AttributeModel,
  AttributeTypeIds,
  ProductModel,
  AssetTypes,
  SearchProductModel,
} from '@cpak/adp-cpak-products-service-models';
import {
  BOMModel,
  BOMPartModel,
  GetPaksModel,
  LocationModel,
  LocationTypes,
  PlantsEnum,
  ProcedureModel,
  PrototypeStatuses,
  QuoteStatuses,
  QuoteModel,
  RelationTypeEnum,
  TrackedActionCodes,
  TrackedActionModel,
  RelationModel,
  PrototypeModel,
  TrackedActionTypes,
  SearchPrototypeModel,
  SearchQuoteModel,
} from '@cpak/adp-cpak-quotes-service-models';
import { defaultMaxCharactersForHouston, NO_IMAGE_URL, plantToMaxCharacters, ProductStatusConfig } from '@cpak/consts';

import { deepCopy as deepCopyTs } from 'deep-copy-ts';
import { fromEvent, Observable, take } from 'rxjs';
import { AbstractControl, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { TRUE_PRODUCT_ATTRIBUTE_VALUES } from '@cpak/consts';
import { Translation, TranslocoService } from '@ngneat/transloco';
import { EntityWithBOM } from '../../main-features-lib/src/pak/interfaces/bom-host';
import { pendingForPriceApprovalStatuses, secondLocationToFirstLocation } from '../../consts';
import { PackModel } from '../../main-features-lib/src/pak/interfaces/pak.interface';
import { getAvailableProductLocations } from './product-locations.utils';
import { HttpResponse } from '@angular/common/http';
import { IrisConfig } from '../../interfaces';
import { IrisStatusConfig, unknownIrisStatusConfig } from '../../consts';
import { ComparisonViewPak } from '../../main-features-lib/src/pak/interfaces/comparison-view.interface';
import { ProductStatus, UserRoleType } from '@cpak/enums';
import { CustomMessageService } from '@cpak/main-features-lib/services';
import { RTP_ALLOWED_STATUSES, CurrencyConversionUtils } from '@cpak/adp-cpak-pricing-service-shared';
import { Currencies } from '@cpak/enums';
import Decimal from 'decimal.js';

export function utils(): string {
  return 'utils';
}

export function getProductVendor(product: ProductModel): string | undefined {
  return product.attributes.find((attr: AttributeModel) => attr.attributeTypeId === AttributeTypeIds.Vendor)?.value;
}

// @TODO this function should be removed and completely replaced by getAttributeValue
export function getProductAttribute(
  attributes: AttributeModel[],
  productAttribute: AttributeTypeIds
): string | undefined {
  return attributes.find((attr: AttributeModel) => attr.attributeTypeId === productAttribute)?.value;
}

export function getAccountIrisNumber(account: AccountModel): string | undefined {
  return account.externalIds?.find((externalId: ExternalIdModel) => externalId.type === 'account:iris')?.externalId;
}

export function getAccountSFNumber(account: AccountModel): string | undefined {
  return account.externalIds?.find((externalId: ExternalIdModel) => externalId.type === 'account:sfobject')?.externalId;
}

export function getAccountProcedureName(
  quote: QuoteModel | EntityWithBOM,
  procedures: ProcedureModel[]
): string | undefined {
  return procedures.find((procedure: ProcedureModel) => procedure.id === quote.procedureId)?.name;
}

export function getAccountById(accounts: AccountModel[] = [], id?: string | null): AccountModel | undefined {
  return id ? accounts.find((account: AccountModel) => account.id === id) : undefined;
}

export function getPartStatusIfNoProductType(data: {
  attributes: AttributeModel[];
  countryId: string;
  startResponse: StartResponsePayload | undefined | null;
}): { partStatus: number | null; statusIsShown: boolean } {
  const { attributes, countryId, startResponse } = data;

  if (!startResponse) return { partStatus: null, statusIsShown: true };

  const plant = getPlantFromCountryId(startResponse.data.config.plantConfig, countryId)?.plant;

  const quotesConfig = startResponse.data.config.quotesConfig;

  const allLocations =
    quotesConfig.find(config => config.plant === plant)?.quoteProducts.flatMap(product => product.locations) ?? [];

  if (!plant) return { partStatus: null, statusIsShown: true };

  const availableLocations = getAvailableProductLocations({
    attributes,
    locations: allLocations,
    countryId,
    plant,
  });

  const defaultLocationId = getProductDefaultLocation(attributes, plant);
  const allowedLocations = getProductAllowedLocations(attributes, plant)
    .map(locationId => allLocations.find(location => location.id === locationId))
    .filter((location): location is LocationModel => !!location);

  const defaultLocation = defaultLocationId
    ? availableLocations.find(location => location.id === defaultLocationId)
    : null;

  const allowedLocationTypes = new Set(availableLocations?.map(location => location.type));

  if (!allowedLocations || allowedLocations.length === 0 || !defaultLocationId) {
    return { partStatus: null, statusIsShown: true };
  }

  if (allowedLocationTypes.size > 1) {
    return { partStatus: null, statusIsShown: false };
  }

  const location = defaultLocation ?? availableLocations[0];

  const partStatus = getPartStatus({
    attributes,
    countryId,
    plant,
    locations: allLocations,
    locationId: location?.id ?? defaultLocationId,
  });

  return { partStatus, statusIsShown: true };
}

export function normalizeToWhereUsedItem(
  product: ProductModel,
  countryId: string,
  partNumberDisplay: AttributeTypeIds,
  startResponse: StartResponsePayload | undefined | null
): WhereUsedItem {
  const { partStatus, statusIsShown } = getPartStatusIfNoProductType({
    attributes: product.attributes,
    countryId,
    startResponse,
  });

  return {
    id: product.id,
    name: product.name,
    vendor: getProductVendor(product) || '',
    partNumber: getProductAttribute(product.attributes, partNumberDisplay) || '',
    partStatus,
    itemImage: getProductSmallImage(product),
    containsAllergens: isProductContainsAllergens(product),
    statusIsShown,
  };
}

export function getProductSmallImage(product: ProductModel | SearchProductModel): string {
  return (
    product.assets.find(asset => asset.type === AssetTypes.image)?.sources.find(source => source.location)?.location ||
    NO_IMAGE_URL
  );
}

export function trimFieldSpaces(className: string): void {
  const elements = document.querySelectorAll(className);
  elements.forEach(element => {
    const content = element.textContent?.trim() || '';
    element.innerHTML = content;
  });
}

export function toAbsoluteUrl(url: string): string {
  let res;
  let urlIsAbsoluteRegex = new RegExp('^(?:[a-z+]+:)?//', 'i');
  if (urlIsAbsoluteRegex.test(url)) {
    res = url;
  } else {
    res = location.origin;
    if (url[0] === '/') {
      res += url;
    } else {
      res += '/' + url;
    }
  }
  return res;
}

export function getImageFromCatalogAssets(assets: AssetModel[]): string {
  const imageUrl =
    assets.find(asset => asset.type === AssetTypes.image)?.sources.find(source => !!source.location)?.location ||
    NO_IMAGE_URL;
  return toAbsoluteUrl(imageUrl);
}

export function isAlconProductVendor(product: ProductModel): boolean {
  const alconVendorAttr = getProductAttribute(product.attributes, AttributeTypeIds.AlconVendor);
  return alconVendorAttr != null && TRUE_PRODUCT_ATTRIBUTE_VALUES.includes(alconVendorAttr);
}

export function getQuoteFromQuoteData(quoteData: QuotePageData, quoteId: string): QuoteModel | undefined {
  return quoteData.quotes.find(quote => quote.id === quoteId);
}

export function getPrototypeFromQuoteData(
  prototypeData: PrototypePageData,
  prototypeId: string
): PrototypeModel | undefined {
  return prototypeData.prototypes.find(prototype => prototype.id === prototypeId);
}

export function getAccountFromQuoteData(
  quoteData: QuotePageData | PrototypePageData,
  accountId?: string | null
): AccountModel | undefined {
  return quoteData.accounts?.find(account => account.id === accountId);
}

export function getPakCountryId(account?: AccountModel | null, quote?: QuoteModel | null): string | undefined {
  if (account?.countryId && account?.countryId !== '') {
    return account.countryId;
  }
  return quote?.countryId;
}

export function getPrototypeCountryId(pageData: PrototypePageData, prototype: PrototypeModel): string | undefined {
  const quote = getQuoteFromQuoteData(pageData, prototype.quoteId);
  const account = getAccountFromQuoteData(pageData, quote?.accountId);
  return getPakCountryId(account, quote);
}

export function getProcedureIdsForProduct(
  productTypes: QuoteProductModel[],
  productId: string
): ProcedureModel[] | undefined {
  return productTypes.find(product => product.id === productId)?.procedures;
}

export function getBOMFromQuoteData(quoteData: QuotePageData, bomId?: string): BOMModel | undefined {
  return quoteData.boms.find(bom => bom.id === bomId);
}

export function isTouchDevice(): boolean {
  return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
}

export function getQuoteCreatedActionId(trackedActionsList: TrackedActionModel[]): string | undefined {
  return trackedActionsList.find((action: TrackedActionModel) => action.code === TrackedActionCodes.QuoteCreated)?.id;
}

export function getQuoteDataByActionId(quotes: QuoteModel[], actionId?: string): QuoteModel | null {
  return (actionId && quotes.find(quote => quote.actionIds.includes(actionId))) || null;
}

export function getMaxCharactersBasedOnPlant(plant?: string, accountId?: string): number {
  if (accountId && plant === PlantsEnum.puurs) {
    return plantToMaxCharacters.get(PlantsEnum.puurs) || defaultMaxCharactersForHouston;
  } else {
    return defaultMaxCharactersForHouston;
  }
}

export function getPlantFromCountryId(
  plantData: PlantConfigData[],
  countryId?: string | null
): PlantConfigData | undefined {
  return plantData.find(plantConfig => plantConfig.countryIds?.find(id => id === countryId));
}

export function isQuoteContract(
  quote: QuoteModel | GetPaksModel | PrototypeModel | ComparisonViewPak | SearchPrototypeModel | SearchQuoteModel
): boolean {
  return quote.status === QuoteStatuses.contractCompleted;
}

export function isQuotePrototype(
  quote: QuoteModel | GetPaksModel | PrototypeModel | ComparisonViewPak | SearchPrototypeModel | SearchQuoteModel
): boolean {
  return quote.status === PrototypeStatuses.prototypeCompleted;
}

export function isPackQuotePrototype(quote: PackModel): quote is QuoteModel | PrototypeModel {
  return !!(quote as QuoteModel | PrototypeModel).status;
}

export function getQuotesConfigForPlant(
  quotesConfig: QuoteConfigData[],
  plantId?: string
): QuoteConfigData | undefined {
  return quotesConfig.find(quoteConfig => quoteConfig.plant === plantId);
}

export function getQuoteRelationFromQuoteData(
  quoteId: string,
  quoteData: QuotePageData,
  relationType: RelationTypeEnum
): RelationModel | undefined {
  return quoteData.quoteRelations?.find(
    relation => relation.type === relationType && relation.secondaryQuoteId === quoteId
  );
}

export function isQuoteParent(quoteId: string, quoteData: QuotePageData): boolean {
  return (
    quoteData.quoteRelations?.some(
      relation => relation.type === RelationTypeEnum.parent && relation.primaryQuoteId === quoteId
    ) || false
  );
}

export function isSisterAsName(sistersConfig: SistersConfigData, countryCode?: string): boolean {
  return sistersConfig.sisterAsNameCountryIds.includes(countryCode || '') || false;
}

export function getProductType(
  productTypeId: string,
  productTypes: QuoteProductModel[]
): QuoteProductModel | undefined {
  return productTypes.find(productType => productType.id === productTypeId);
}

export function getProductCountryStatusCode(attributes: AttributeModel[], countryId: string): number | null {
  const productCountriesStatuses = getProductAttribute(attributes, AttributeTypeIds.CountryStatus);
  if (productCountriesStatuses) {
    const countryStatus = parseJSON<{ [key: string]: string }>(productCountriesStatuses);
    return Number(typeof countryStatus === 'object' ? countryStatus[countryId] : countryStatus);
  }
  return null;
}

export function getRatingAttributeValue(data: {
  attributes: AttributeModel[];
  ratingType: AttributeTypeIds;
  plant: PlantsEnum | undefined;
}): number {
  const ratingsAttribute = data.attributes.find((attr: AttributeModel) => attr.attributeTypeId === data.ratingType);

  if (!ratingsAttribute || !data.plant) return 0;

  const partRating = getAttributeValue(ratingsAttribute, data.plant);

  const parsedRating = Number(partRating);

  if (!Number.isNaN(parsedRating)) return parsedRating;

  return 0;
}

// @TODO this function should be removed and completely replaced by getAttributeValue
export function getProductAllowedLocations(attributes: AttributeModel[], plant: PlantsEnum): string[] {
  const productAllowedLocations = getProductAttribute(attributes, AttributeTypeIds.AllowedLocations);
  if (productAllowedLocations) {
    const parsedLocations = parseJSON<{ [key: string]: string[] | string } | string[]>(productAllowedLocations);
    if (typeof parsedLocations === 'object' && parsedLocations !== null) {
      if (Array.isArray(parsedLocations)) {
        return parsedLocations;
      } else {
        const locations = parsedLocations[plant];
        return Array.isArray(locations) ? locations : [locations];
      }
    }
  }
  return [];
}

export function getProductCountryInclusionList(attributes: AttributeModel[]): string[] {
  const productCountryInclusion = getProductAttribute(attributes, AttributeTypeIds.CountryInclusion);

  if (!productCountryInclusion) {
    return [];
  }

  const parsedCountryInclusion = parseJSON<string[]>(productCountryInclusion);

  return Array.isArray(parsedCountryInclusion) ? parsedCountryInclusion : [parsedCountryInclusion];
}

// @TODO this function should be removed and completely replaced by getAttributeValue
export function getProductDefaultLocation(attributes: AttributeModel[], plant: PlantsEnum): string | null {
  const productDefaultLocation = getProductAttribute(attributes, AttributeTypeIds.DefaultLocation);
  if (productDefaultLocation) {
    const value = parseJSON<{ [key: string]: string }>(productDefaultLocation);
    return typeof value === 'object' ? value[plant] : value;
  }
  return null;
}

export function hasLocationTypeMain(productTypes: QuoteProductModel[], selectedProductType?: string): boolean {
  const locations = (selectedProductType && getProductType(selectedProductType, productTypes)?.locations) || [];
  return locations.filter((location: LocationModel) => location.type === LocationTypes.main).length > 0;
}

export function getProductStatusConfig(countryStatus?: number | null): ProductConfig {
  return (
    (countryStatus && ProductStatusConfig.get(countryStatus)) || ProductStatusConfig.get(ProductStatus.UNAVAILABLE)!
  );
}

export function getIrisStatusConfig(irisStatus?: number | null): IrisConfig | undefined {
  if (irisStatus == null) return;
  return IrisStatusConfig.get(irisStatus) || unknownIrisStatusConfig;
}

export function deepCopy<T>(obj: T): T {
  return deepCopyTs<T>(obj);
}

export function isCorePack(quoteId: string, quoteData: QuotePageData): boolean {
  return (
    !!quoteData.quoteRelations?.find(
      relation => relation.type === RelationTypeEnum.sister && relation.primaryQuoteId === quoteId
    ) &&
    !!quoteData.quoteRelations?.find(
      relation => relation.type === RelationTypeEnum.parent && relation.secondaryQuoteId !== quoteId
    )
  );
}

export function setMarginForMenu(menuButton: HTMLElement, menu: HTMLElement): void {
  const menuButtonRect = menuButton.getBoundingClientRect();
  const menuTopRect = menu.getBoundingClientRect();
  const buttonHeight = menuButton.offsetHeight;
  const buttonWidth = menuButton.offsetWidth;

  // menu appears below the button
  if (menuButtonRect.top < menuTopRect.top) {
    menu.style.marginTop = `-${buttonHeight}px`;
  } else {
    // menu appears above the button
    menu.style.marginTop = `${buttonHeight}px`;
  }

  // menu appears on the right side the button
  if (isMenuAtRight(menuButton, menu)) {
    menu.style.marginLeft = `${buttonWidth}px`;
  } else {
    // menu appears on the left side the button
    menu.style.marginLeft = `-${buttonWidth}px`;
  }
}

export function isMenuAtRight(menuButton: HTMLElement, menu: HTMLElement): boolean {
  const menuButtonRect = menuButton.getBoundingClientRect();
  const menuTopRect = menu.getBoundingClientRect();
  return menuTopRect.left >= menuButtonRect.left;
}

export function tooltipViewHandler(event: Event, element: HTMLElement): void {
  element.dispatchEvent(new MouseEvent('mouseenter'));

  fromEvent(document, 'touchstart')
    .pipe(take(1))
    .subscribe(() => {
      element.dispatchEvent(new MouseEvent('mouseleave'));
    });

  event.preventDefault();
}

export function generateKeyForChangedProductsSet(productId: string, location: string): string {
  return `${productId}_${location}`;
}

export function isArraysEqual(firstArray: unknown[], secondArray: unknown[]): boolean {
  return (
    firstArray.every(firsArrayItem => secondArray.includes(firsArrayItem)) && firstArray.length === secondArray.length
  );
}

// export function setMarginTopForSlideMenu(menuButton: HTMLElement, menu: ElementRef): void {
//   const menuButtonTop = menuButton.getBoundingClientRect().top;
//   const menuButtonLeft = menuButton.getBoundingClientRect().left;
//   const menuLeft = menu.nativeElement.getBoundingClientRect().left;
//   const menuTop = menu.nativeElement.getBoundingClientRect().top;
//   const buttonHeight = menuButton.offsetHeight;
//   const buttonWidth = menuButton.offsetWidth;
//   // menu appears below the button
//   if (menuButtonTop < menuTop) {
//     menu.nativeElement.style.marginTop = `-${buttonHeight}px`;
//   } else {
//     // menu appears above the button
//     menu.nativeElement.style.marginTop = `${buttonHeight}px`;
//   }
//   // menu appears from left side of the button
//   if (menuButtonLeft > menuLeft) {
//     menu.nativeElement.style.marginLeft = `-${buttonWidth + 11}px`;
//   } else {
//     // menu appears from right side of the button
//     menu.nativeElement.style.marginLeft = `${buttonHeight + 11}px`;
//   }
// }

export function isUserQuoteOwner(ownerId: string, userId: string): boolean {
  return ownerId === userId;
}

export function isProductContainsAllergens(product: ProductModel): boolean {
  return product.attributes.some(
    attr =>
      (attr.attributeTypeId === AttributeTypeIds.Latex || attr.attributeTypeId === AttributeTypeIds.DryNaturalRubber) &&
      TRUE_PRODUCT_ATTRIBUTE_VALUES.includes(attr.value)
  );
}

export function getFieldValueWithUserTimestamp(fieldValue: string, username: string): string {
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const currentDate = new Date();

  let localDateTime = [
    currentDate.getFullYear(),
    ('0' + (currentDate.getMonth() + 1)).slice(-2),
    ('0' + currentDate.getDate()).slice(-2),
  ].join('/');

  localDateTime += ' ' + currentDate.toLocaleTimeString();
  const timestamp = `--${username}: ${localDateTime} (${timezone})`;

  return `${fieldValue}${fieldValue ? '\n' : ''}\n${timestamp}:\n`;
}

export function getFieldValueWithUserTimestampForBulk(username: string): string {
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const currentDate = new Date();

  let localDateTime = [
    currentDate.getFullYear(),
    ('0' + (currentDate.getMonth() + 1)).slice(-2),
    ('0' + currentDate.getDate()).slice(-2),
  ].join('/');

  localDateTime += ' ' + currentDate.toLocaleTimeString();
  const timestamp = `--${username}: ${localDateTime} (${timezone})`;
  return `${timestamp}:`;
}

export function noOnlyWhiteSpaceValidation(control: AbstractControl): ValidationErrors | null {
  return control.value.length === 0 || control.value.trim().length > 0 ? null : { whitespace: true };
}

export function requiredValidation(control: AbstractControl): ValidationErrors | null {
  return control.touched ? Validators.required(control) : null;
}

export function markFormGroupTouched(formGroup?: FormGroup): void {
  if (formGroup) {
    formGroup.markAllAsTouched();
    Object.values(formGroup.controls).forEach(control => {
      control.updateValueAndValidity({ onlySelf: true });
    });
    formGroup.updateValueAndValidity();
  }
}

export function fileTypeValidation(supportedExtensions: string[]) {
  return (control: FormControl<File>): ValidationErrors | null => {
    const extension = getFileExtension(control.value);
    if (extension === '' || supportedExtensions.includes(extension)) {
      return null;
    }

    return { unsupportedExtension: true };
  };
}

export function getFileExtension(file?: File): string {
  const fileName = file?.name.split('\\').slice(-1)[0];
  return fileName?.split('.').slice(-1)[0] || '';
}

export function sortByObjectProperty<T, K extends keyof T>(array: T[], propName: K): T[] {
  return array.sort((a, b) => (a[propName] > b[propName] ? 1 : b[propName] > a[propName] ? -1 : 0));
}

export function bomMatcher(b1: BOMPartModel, b2: BOMPartModel): boolean {
  return b1.locationId === b2.locationId && b1.productId === b2.productId;
}

export function bomMatcherConsolidated(
  revisionBomLocation: string,
  revisionProductId: string,
  parentBomLocation: string,
  parentProductId: string
): boolean {
  return revisionBomLocation === parentBomLocation && revisionProductId === parentProductId;
}

export function getIsBomDisabled(quote: QuoteModel, userId: string): boolean {
  return (
    !isUserQuoteOwner(quote.ownerId, userId) ||
    isQuoteContract(quote) ||
    pendingForPriceApprovalStatuses.has(quote.status)
  );
}

export function getTimeDifference(givenTime: number, translation: Translation): string {
  const difference_ms = (Date.now() - givenTime) / 1000;
  const seconds = Math.floor(difference_ms % 60);
  const days = Math.floor(difference_ms / (3600 * 24));
  const hours = Math.floor((difference_ms % (3600 * 24)) / 3600);
  const minutes = Math.floor((difference_ms % 3600) / 60);
  const dDisplay = days > 0 ? days + (days === 1 ? ` ${translation['day']} ` : ` ${translation['days']} `) : '';
  const hDisplay = hours > 0 ? hours + (hours == 1 ? ` ${translation['hour']} ` : ` ${translation['hours']} `) : '';
  const mDisplay =
    minutes > 0 ? minutes + (minutes == 1 ? ` ${translation['minute']} ` : ` ${translation['minutes']} `) : '';
  const sDisplay =
    seconds > 0 ? seconds + (seconds == 1 ? ` ${translation['second']} ` : ` ${translation['seconds']} `) : '';
  return dDisplay + hDisplay + mDisplay + sDisplay;
}

export function getNonSterilePrototypeNumber(prototype: PrototypeModel): string | null {
  return prototype?.instructions.nonSterileQuantity !== undefined ? prototype?.number.replace(/Q/gi, 'QNS') : null;
}

export function getLastUpdate(actions: TrackedActionModel[]): TrackedActionModel | undefined {
  return descendingSortActionsByDate(actions).find(
    action => action.type === TrackedActionTypes.User && action.code !== TrackedActionCodes.TakeOwnership
  );
}

export function getLastUpdateDate(actions: TrackedActionModel[]): number | undefined {
  return getLastUpdate(actions)?.date;
}

export function getLocationToTypeMap(
  quoteProducts: QuoteProductModel[],
  quoteProductId: string
): Map<string, LocationTypes> {
  const locations = quoteProducts.find(productConfig => productConfig.id === quoteProductId)?.locations;
  const locationsMap = new Map<string, LocationTypes>();
  locations?.forEach(location => locationsMap.set(location.id, location.type));
  return locationsMap;
}

export function setCategoryImageUrl(URLObject: URL, width: string, height: string): string {
  URLObject.searchParams.append('width', width);
  URLObject.searchParams.append('height', height);

  return URLObject.href;
}

export function getCountryConfigByCountryCode(
  countryId: string,
  countryConfigData: CountryConfigData[]
): CountryConfigData | undefined {
  return countryConfigData.find(country => country.id === countryId);
}

export function getQuoteRelations(pakData: QuotePageData | BOMPageData): RelationModel[] | undefined {
  return (pakData as QuotePageData).quoteRelations;
}

export function checkSecondLocationIsAvailable(locationId: string, existedLocations: string[]): boolean {
  const firstLocationId = secondLocationToFirstLocation.get(locationId);
  return !firstLocationId || existedLocations.includes(firstLocationId);
}

//TODO: use this utils in add parts and replacing with dialog
export function mergeProductDataToPackData(
  packData: Partial<BOMPageData>,
  productData: ProductPageData
): Partial<BOMPageData> {
  if (packData.products!.findIndex(v => v.id === productData.product.id) === -1) {
    packData.products!.push(productData.product);
  }

  productData.path.forEach(category => {
    if (!packData.categories) packData!.categories = [];
    if (packData.categories.findIndex(v => v.id === category.id) === -1) {
      packData.categories.push(category);
    }
  });

  productData.attributeTypes.forEach(attributeType => {
    if (packData.attributeTypes!.findIndex(v => v.id === attributeType.id) === -1) {
      packData.attributeTypes!.push(attributeType);
    }
  });

  return packData;
}

export function parseJSON<T>(input: string): T | string {
  try {
    return JSON.parse(input);
  } catch {
    return input;
  }
}

export function getLocationIdsFromBom(bom: BOMModel): string[] {
  return (bom.parts.map(part => part.locationId).filter(Boolean) as string[]) || [];
}

export const descendingSortActionsByDate = (trackedActions: TrackedActionModel[]): TrackedActionModel[] => {
  return trackedActions.sort((a, b) => {
    if (!a.date) return -1;
    if (!b.date) return 1;
    return new Date(b.date).getTime() - new Date(a.date).getTime();
  });
};

export const getTrackedActionsListForCurrentQuote = (
  pageData?: QuotePageData | PrototypePageData | null,
  quote?: QuoteModel | PrototypeModel | null
): TrackedActionModel[] => {
  return pageData?.trackedActions?.filter(action => !!quote?.actionIds?.find(id => id === action?.id)) || [];
};

export const getTrackedActionsListForNewlyCreatedQuote = (
  oldPageData: QuotePageData | PrototypePageData,
  newlyCreatedPageData: QuotePageData
): TrackedActionModel[] => {
  return (
    newlyCreatedPageData.trackedActions?.filter(newAction =>
      oldPageData.trackedActions?.every(oldAction => oldAction?.id !== newAction?.id)
    ) || []
  );
};

export const getImageUrlsForCarouselFromAssets = (assets: AssetModel[]): string[] => {
  const imageUrls = assets
    .filter(asset => asset.type === AssetTypes.image)
    .flatMap(asset => asset.sources.map(source => source.location).filter(location => !!location));
  return imageUrls.length > 0 ? imageUrls : [''];
};

export function isTemplatePack(pack: { isSharedPack?: boolean; description?: string }): boolean {
  return !!pack.isSharedPack && (pack.description || '').toLowerCase().includes('template');
}

export function displaySmartRevisions(startResponse: StartResponsePayload, countryId: string = ''): boolean {
  return !!(
    startResponse.data.config.smartRevisionsConfig?.activateSmartRevisions &&
    startResponse.data.config.smartRevisionsConfig.countryIds.includes(countryId) &&
    !(
      startResponse.data.userInfo.user.roles.includes(UserRoleType.CPak_Sales_Rep) &&
      startResponse.data.config.smartRevisionsConfig?.restrictSalesRepsCountryList?.includes(countryId)
    )
  );
}

export function isContractStagedToIris(quoteData: QuotePageData, packId: string): boolean {
  return !!getTrackedActionsListForCurrentQuote(quoteData, getQuoteFromQuoteData(quoteData, packId)).find(
    action => action.code === TrackedActionCodes.SentToIris
  );
}

export function isPrototypeStagedToIris(quoteData: PrototypePageData, packId: string): boolean {
  return !!getTrackedActionsListForCurrentQuote(quoteData, getPrototypeFromQuoteData(quoteData, packId)).find(
    action => action.code === TrackedActionCodes.SentToIris
  );
}

export function convertSortOrder(order: number | undefined): string | undefined {
  if (order === 1) {
    return 'asc';
  }
  if (order === -1) {
    return 'desc';
  }
  return undefined;
}

export function getValueSync<T>(obs: Observable<T>): T | null {
  let result: T | null = null;
  obs.subscribe(val => (result = val)).unsubscribe();
  return result;
}

export type ConvertToForm<T> = {
  [K in keyof T]: FormControl<T[K] | null>;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type FormDefinition<T> = { [k in keyof T]: any[] };

export function getProductManufacturingPlantStatus(data: {
  attributes: AttributeModel[];
  plant: PlantsEnum;
}): number | null {
  const { attributes, plant } = data;
  const manufacturingPlantStatusAttribute = getProductAttribute(attributes, AttributeTypeIds.ManufacturingPlantStatus);
  if (manufacturingPlantStatusAttribute) {
    const value = parseJSON<{ [key: string]: string }>(manufacturingPlantStatusAttribute);
    return typeof value === 'object' ? Number(value[plant]) : Number(value);
  }
  return null;
}

export function getProductCountryInclusion(data: { attributes: AttributeModel[]; countryId: string }): boolean {
  const countryInclusionList = getProductCountryInclusionList(data.attributes);

  return countryInclusionList.includes(data.countryId);
}

export function getPartStatus(data: {
  attributes: AttributeModel[];
  countryId: string;
  plant: PlantsEnum;
  locations: LocationModel[];
  locationId?: string;
}): number | null {
  const { attributes, countryId, plant, locations, locationId } = data;
  const locationType = locations.find(location => location.id === locationId)?.type;

  const allowedLocations = getProductAllowedLocations(attributes, plant);
  const defaultLocationId = getProductDefaultLocation(attributes, plant);

  if (!defaultLocationId || !locationId || !allowedLocations.includes(locationId)) return null;

  switch (locationType) {
    case LocationTypes.main:
      const countryInclusion = getProductCountryInclusion({ attributes, countryId });

      if (!countryInclusion) return getProductManufacturingPlantStatus({ attributes, plant });

      return getProductManufacturingPlantStatus({ attributes, plant });
    case LocationTypes.addAfter:
      return getProductCountryStatusCode(attributes, countryId);
    default:
      return null;
  }
}

export function getFileNameFromHeader<T>(httpResponse: HttpResponse<T>): string | undefined {
  return (httpResponse.headers.get('content-disposition') || '')
    .split(';')
    .reverse()
    .find(part => part.includes('filename='))
    ?.replace('filename=', '')
    .trim();
}

export const getParentQuote = (quoteData: QuotePageData, quote: QuoteModel): QuoteModel | null => {
  const parentQuoteId = getQuoteRelationFromQuoteData(quote.id, quoteData, RelationTypeEnum.parent)?.primaryQuoteId;
  return (parentQuoteId && getQuoteFromQuoteData(quoteData, parentQuoteId)) || null;
};

export const plantSpecificAttributes: AttributeTypeIds[] = [
  AttributeTypeIds.AllowedLocations,
  AttributeTypeIds.DefaultLocation,
  AttributeTypeIds.IrisAddAfterItemType,
  AttributeTypeIds.QuoteProductType,
  AttributeTypeIds.GreenRating,
  AttributeTypeIds.PartRating,
  AttributeTypeIds.Visible,
];

type AttributeValue = string | string[];
export type PlantSpecificAttributeValue = Record<PlantsEnum, AttributeValue>;
export function getAttributeValue(attribute: AttributeModel, plant?: PlantsEnum): AttributeValue {
  try {
    let parsedJson = parseJSON<unknown>(attribute.value);

    if (
      plant &&
      plantSpecificAttributes.includes(attribute.attributeTypeId as AttributeTypeIds) &&
      isPlantSpecificAttributeValue(parsedJson)
    ) {
      return parsedJson[plant];
    }

    return parsedJson as AttributeValue;
  } catch {
    return [];
  }
}

function isPlantSpecificAttributeValue(value: unknown): value is PlantSpecificAttributeValue {
  return typeof value === 'object' && value !== null && (PlantsEnum.puurs in value || PlantsEnum.houston in value);
}

export const getTimeZoneOffset = (timeZone: string): number => {
  const date = new Date();
  const utcDate = new Date(date.toLocaleString('en-US', { timeZone: 'UTC' }));
  const tzDate = new Date(date.toLocaleString('en-US', { timeZone }));
  return (tzDate.getTime() - utcDate.getTime()) / 6e4;
};

export const getTimeZoneGMTByOffset = (offset: number): string => {
  const convertGmtBaseOnZeroTimeZone = (n: number): string => (n < 10 ? '0' : '') + n;
  const sign = offset > 0 ? '+' : '-';
  const offsetWithoutSign = Math.abs(offset);
  return `${sign}${convertGmtBaseOnZeroTimeZone((offsetWithoutSign / 60) | 0)}:${convertGmtBaseOnZeroTimeZone(
    offsetWithoutSign % 60
  )}`;
};

export const addPartIdToBOM = <T extends BOMModel>(bom: T | undefined): T => {
  // Add UUID to part array since the methods within pricing
  // utils use a MAP which cannot have duplicate ids.
  // New BOM items have no ids and hence treated as duplicate
  // records and one of them is ignored
  const updatedParts = bom?.parts.map(part => {
    if (part.id) {
      return part;
    } else {
      return { ...part, id: uuidv4() };
    }
  });
  return { ...bom!, parts: updatedParts! };
};

export const displayQuoteArchiveMessages = (
  archived: boolean,
  translocoService: TranslocoService,
  quoteNumber: string,
  customMessageService: CustomMessageService,
  quoteStatus: QuoteStatuses | PrototypeStatuses,
  rtpLiveCountryIds: string[],
  quoteCountryId: string | undefined
): void => {
  const messageKey = archived ? 'updatePackArchived' : 'updatePackUnarchived';
  const message = translocoService.translate(messageKey, { packNumber: quoteNumber });
  customMessageService.showMessage(message, 'custom-success');

  if (
    archived === false &&
    RTP_ALLOWED_STATUSES.includes(quoteStatus as QuoteStatuses | PrototypeStatuses) &&
    quoteCountryId &&
    rtpLiveCountryIds.includes(quoteCountryId)
  ) {
    const messageKey = 'rtpMessageForContractUnarchive';
    const message = translocoService.translate(messageKey, { packNumber: quoteNumber });

    customMessageService.showMessage(message, 'custom-success', undefined, undefined, true);
  }
};

function isQuoteModel(quote: QuoteModel | PrototypeModel): quote is QuoteModel {
  return 'trackedActionIds' in quote;
}
export const getIrisTrackedActionsListForCurrentQuote = (
  pageData?: QuotePageData | PrototypePageData | null,
  quote?: QuoteModel | PrototypeModel | null
): TrackedActionModel[] => {
  let tracks: string[] | undefined;

  if (quote) {
    if (isQuoteModel(quote)) {
      tracks = quote.trackedActionIds;
    } else {
      tracks = quote.actionIds;
    }
  }

  return pageData?.trackedActions?.filter(action => !!tracks?.find(id => id === action?.id)) || [];
};

export function getLocationTypeMapping(location: string): string {
  const locationTypeMapping: { [key: string]: string } = {
    'Main Pak': 'main',
    'Add After': 'addAfter',
    'Gown Pak': 'main',
    'Prep Tray': 'main',
    'Gown Pak2': 'main',
    'Prep Tray2': 'main',
  };

  return locationTypeMapping[location];
}

export function getPartListPrices(
  product: ProductModel,
  plant: PlantsEnum,
  userCurrency: Currencies
): Map<string, number> {
  const partListPrices = new Map<string, number>();

  product.partsPricing?.forEach(part => {
    if (part) {
      const defaultLocation = getProductDefaultLocation(
        product.attributes,
        plant === PlantsEnum.houston ? PlantsEnum.houston : PlantsEnum.puurs
      );

      if (
        getLocationTypeMapping(defaultLocation ?? '') === part.locationType &&
        product.attributes.find(attribute => attribute.attributeTypeId === AttributeTypeIds.ManufacturingNumber)
          ?.value === part.mfgNumber
      ) {
        const listPrice =
          userCurrency === Currencies.local
            ? CurrencyConversionUtils.convertBaseToLocalCurrency(deepCopy(part), new Decimal(part.conversionRate || 1))
                .listMinus
            : part.listMinus;

        if (listPrice !== undefined) {
          partListPrices.set(part.mfgNumber, listPrice);
        }
      }
    }
  });

  return partListPrices;
}

export function getPartListPricesForSearchResults(
  product: SearchProductModel,
  plant: PlantsEnum,
  userCurrency: Currencies
): { manufacturingNumber: string; listMinus: number } | undefined {
  if (!product.partsPricing) {
    return undefined;
  }

  for (const partPricing of product.partsPricing) {
    if (partPricing) {
      const defaultLocation = getProductDefaultLocation(
        product.attributes ?? [],
        plant === PlantsEnum.houston ? PlantsEnum.houston : PlantsEnum.puurs
      );

      if (
        getLocationTypeMapping(defaultLocation ?? '') === partPricing.locationType &&
        product.manufacturingNumber === partPricing.mfgNumber
      ) {
        const listPrice =
          userCurrency === Currencies.local
            ? CurrencyConversionUtils.convertBaseToLocalCurrency(
                deepCopy(partPricing),
                new Decimal(partPricing.conversionRate || 1)
              ).listMinus
            : partPricing.listMinus;

        if (listPrice !== undefined) {
          return { manufacturingNumber: product.manufacturingNumber, listMinus: listPrice };
        }
      }
    }
  }
  return undefined;
}

export function getDeviceType(userAgent: string): string {
  if (/mobile/i.test(userAgent)) {
    return 'Mobile';
  } else if (/tablet|ipad|playbook|silk/i.test(userAgent)) {
    return 'Tablet';
  } else {
    return 'Desktop';
  }
}

export function getBrowserName(userAgent: string): string {
  if (/chrome|crios|crmo/i.test(userAgent) && !/edg/i.test(userAgent) && !/opr/i.test(userAgent)) {
    return 'Chrome';
  } else if (/firefox|fxios/i.test(userAgent)) {
    return 'Firefox';
  } else if (/safari/i.test(userAgent) && !/chrome|crios|crmo|android/i.test(userAgent)) {
    return 'Safari';
  } else if (/opr\//i.test(userAgent)) {
    return 'Opera';
  } else if (/edg/i.test(userAgent)) {
    return 'Edge';
  } else if (/msie|trident/i.test(userAgent)) {
    return 'Internet Explorer';
  }

  return 'Unknown';
}
